import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  Allergen,
  Group,
  Language,
  languageSchema,
  languageSchemaOptional,
  onQueryError,
  onQuerySuccess,
  Product,
  Tag,
  TaxGroup,
  UpdateProduct,
  useAllergens,
  useProduct,
} from '@/core';

const schema = z.object({
  name: languageSchema,
  description: languageSchemaOptional,
  base_price: z.string().nonempty({ message: t('GLOBAL.PRICE_REQUIRED') }),
  isActive: z.boolean(),
  groups: z.array(z.custom<Group>()),
  tags: z.array(z.custom<Tag>()),
  tax_group: z.object({
    id: z.number(),
    value: z.string(),
  }),
  price: z.number().nonnegative(),
  allergens: z.array(z.custom<Allergen>()),
  plu_code: z.string(),
  takeout_packaging_surcharge: z.string(),
});

type EditProductFormValues = {
  name: Language;
  description: Language;
  base_price: string;
  isActive: boolean;
  image_id?: number;
  groups: Group[];
  tags: Tag[];
  tax_group: TaxGroup;
  price: number;
  allergens: Allergen[];
  plu_code: string;
  takeout_packaging_surcharge: string;
};

const mapEditProductToApiForm = (editProduct: EditProductFormValues): UpdateProduct => {
  const {
    name,
    description,
    base_price,
    isActive,
    groups,
    tags,
    image_id,
    tax_group,
    allergens,
    plu_code,
    takeout_packaging_surcharge,
  } = editProduct;
  return {
    name,
    description,
    base_price,
    status: isActive ? 'active' : 'hidden',
    tax_id: tax_group.id,
    image_id,
    group_ids: groups.map((g) => g.id),
    tag_ids: tags.map((t) => t.id),
    allergen_ids: allergens.map((a) => a.id),
    plu_code,
    takeout_packaging_surcharge: takeout_packaging_surcharge || '0',
  };
};

const getInitialValues = (initialProduct?: Product): EditProductFormValues => {
  if (!initialProduct) {
    return {
      name: { en: '', cs: '', de: '' },
      description: { en: '', cs: '', de: '' },
      base_price: '0',
      isActive: true,
      image_id: undefined,
      groups: [],
      tags: [],
      tax_group: { id: 0, value: '21' },
      price: 0,
      allergens: [],
      plu_code: '',
      takeout_packaging_surcharge: '',
    };
  }
  return {
    name: initialProduct.name,
    description: initialProduct.description || { cs: '', en: '', de: '' },
    base_price: initialProduct.base_price,
    isActive: initialProduct.status === 'active',
    image_id: initialProduct.image_id,
    groups: initialProduct.groups || [],
    tags: initialProduct.tags || [],
    tax_group: initialProduct.tax_group || { id: 0, value: '21' },
    price: Number(initialProduct.final_price) || Number(initialProduct.base_price) || 0,
    allergens: [],
    plu_code: initialProduct.plu_code || '',
    takeout_packaging_surcharge: initialProduct.takeout_packaging_surcharge ?? '',
  };
};

export const useProductForm = (initialProduct?: Product) => {
  const queryClient = useQueryClient();
  const { allergensResult } = useAllergens();
  const methods = useForm<EditProductFormValues>({
    defaultValues: getInitialValues(initialProduct),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (allergensResult.data && initialProduct) {
      const allergens = allergensResult.data.filter((a) =>
        initialProduct.allergen_ids?.includes(a.id),
      );
      methods.reset(
        {
          ...getInitialValues(initialProduct),
          allergens,
        },
        { keepDirtyValues: true },
      );
    }
  }, [allergensResult.data, initialProduct, methods]);

  const {
    mutations: { updateMutation },
  } = useProduct();

  const { id } = useParams<{ id: string }>();
  const productId = Number(id);

  const onSubmit = async (values: EditProductFormValues) => {
    if (!id || isNaN(productId)) return;
    const data = mapEditProductToApiForm(values);
    const updateResponse = await updateMutation.mutateAsync({ id: productId, data });
    if (updateResponse.status === 200 && updateResponse.data) {
      onQuerySuccess({
        queryKeysToInvalidate: [['product', productId]],
        successMessage: t('GLOBAL.SAVED'),
        queryClient,
      });
    } else {
      onQueryError({ errorMessage: t('PRODUCT.SAVE_FAILURE') });
    }
  };

  return { methods, onSubmit, isPending: updateMutation.isPending };
};
